import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Lock, ShieldCheck, Eye, EyeOff } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

export default function BankDetails() {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ bank_name: '', account_name: '', account_number: '', mobile_money_number: '' });
  const [revealDialog, setRevealDialog] = useState(false);
  const [password, setPassword] = useState('');
  const [revealing, setRevealing] = useState(false);
  const [revealed, setRevealed] = useState(null);

  const fetchDetails = () => {
    return api.get('/bank-details').then(r => {
      const d = r.data;
      setDetails(d);
      setForm({ bank_name: d.bank_name || '', account_name: d.account_name || '', account_number: '', mobile_money_number: '' });
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { fetchDetails(); }, []);

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post('/bank-details', form);
      toast.success(r.data.message);
      await fetchDetails();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleReveal = async () => {
    setRevealing(true);
    try {
      const r = await api.post('/bank-details/reveal', { password });
      setRevealed(r.data);
      setPassword('');
      setRevealDialog(false);
      toast.success('Details revealed (this view auto-hides in 30 seconds)');
      setTimeout(() => setRevealed(null), 30000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Incorrect password');
    } finally {
      setRevealing(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div>;

  const isLocked = details?.locked || details?.has_details;
  const display = revealed || details;

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Bank Details</h1>
        <p className="text-slate-500 text-sm mt-0.5">Add your bank or mobile money details for withdrawals</p>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4 text-sm text-slate-600">
        <p className="font-medium text-slate-900">Settlement details live in the Settlement page</p>
        <p className="mt-1">
          Use this page for withdrawal bank/mobile money details. Split settlement approval and Flutterwave subaccount setup are handled under{' '}
          <Link to="/dashboard/settlement" className="text-emerald-700 font-medium hover:underline">
            Settlement
          </Link>
          .
        </p>
      </div>

      {isLocked && (
        <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm flex items-start gap-3" data-testid="bank-locked-notice">
          <Lock size={18} className="mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">Your withdrawal details are locked for security.</p>
            <p className="mt-1 text-amber-700">Only an admin can edit or unlock them. You can view them by confirming your password.</p>
          </div>
        </div>
      )}

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardContent className="p-6">
          {isLocked ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <ShieldCheck size={18} className="text-emerald-600" />
                  <span className="text-sm font-medium text-emerald-700">Details saved and secured</span>
                </div>
                {revealed ? (
                  <Button variant="outline" size="sm" onClick={() => setRevealed(null)} data-testid="bank-hide-btn">
                    <EyeOff size={14} className="mr-1.5" /> Hide details
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => setRevealDialog(true)} data-testid="bank-view-btn">
                    <Eye size={14} className="mr-1.5" /> View full details
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-400">Bank Name</p>
                  <p className="text-sm font-medium text-slate-900" data-testid="bank-name-display">{display?.bank_name || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Account Name</p>
                  <p className="text-sm font-medium text-slate-900" data-testid="account-name-display">{display?.account_name || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Account Number</p>
                  <p className="text-sm font-medium text-slate-900 mono-nums" data-testid="account-number-display">{display?.account_number || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Mobile Money</p>
                  <p className="text-sm font-medium text-slate-900" data-testid="mobile-money-display">{display?.mobile_money_number || '-'}</p>
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Bank Name</Label>
                <Input data-testid="bank-name-input" value={form.bank_name} onChange={e => update('bank_name', e.target.value)} placeholder="e.g. Stanbic Bank Uganda" className="mt-1" />
              </div>
              <div>
                <Label>Account Name</Label>
                <Input data-testid="account-name-input" value={form.account_name} onChange={e => update('account_name', e.target.value)} placeholder="Account holder name" className="mt-1" />
              </div>
              <div>
                <Label>Account Number</Label>
                <Input data-testid="account-number-input" value={form.account_number} onChange={e => update('account_number', e.target.value)} placeholder="Bank account number" className="mt-1" />
              </div>
              <div>
                <Label>Mobile Money Number</Label>
                <Input data-testid="mobile-money-input" value={form.mobile_money_number} onChange={e => update('mobile_money_number', e.target.value)} placeholder="+256..." className="mt-1" />
              </div>
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-800 text-xs">
                Once saved, your withdrawal details will be locked for security. You will need to contact support to make changes.
              </div>
              <Button type="submit" data-testid="save-bank-details-btn" disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 text-white">
                {saving ? 'Saving...' : 'Save & Lock Bank Details'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>

      <Dialog open={revealDialog} onOpenChange={setRevealDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Confirm to view details</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">Enter your account password to reveal your full bank account number and mobile money number.</p>
            <div>
              <Label>Password</Label>
              <Input type="password" value={password} onChange={e => setPassword(e.target.value)} className="mt-1" data-testid="bank-reveal-password" autoFocus />
            </div>
            <Button onClick={handleReveal} disabled={revealing || !password} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="bank-reveal-submit-btn">
              {revealing ? 'Verifying...' : 'Reveal details'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
